import React, { useMemo, useState, useEffect, useCallback } from 'react';
import './LeadMagnetList.css';
import Table from '../../common/Table';
import TextInput from '../../common/TextInput';
import Button from '../../common/Button';
import LoadingSpinner from '../../common/LoadingSpinner';
import Alert from '../../common/Alert';
import Modal from '../../common/Modal';
import { createLeadMagnetColumns } from './leadMagnetColumns';
import { leadMagnetApi } from '../../../services/apis/configuredApis';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';

/**
 * Transform API response from snake_case to camelCase
 */
const transformLeadMagnet = (apiLeadMagnet) => {
  return {
    id: apiLeadMagnet.id,
    internalName: apiLeadMagnet.internal_name,
    status: apiLeadMagnet.status?.toLowerCase() || 'draft',
    slug: apiLeadMagnet.slug,
    pageTitle: apiLeadMagnet.page_title,
    heroHeadline: apiLeadMagnet.hero_headline,
    heroSubHeadline: apiLeadMagnet.hero_subheadline,
    bulletPoints: apiLeadMagnet.bullet_points || [],
    credibilityText: apiLeadMagnet.credibility_text,
    formCtaText: apiLeadMagnet.cta_text,
    seoTitle: apiLeadMagnet.seo_title,
    seoDescription: apiLeadMagnet.seo_description,
    assetId: apiLeadMagnet.asset_id,
    pageType: (apiLeadMagnet.page_type || 'STANDARD').toUpperCase(),
    soundBytes: apiLeadMagnet.sound_bytes || {},
    funnelLinks: apiLeadMagnet.funnel_links || [],
    ctaBox: apiLeadMagnet.cta_box || {},
    createdDate: apiLeadMagnet.created_at,
    lastUpdated: apiLeadMagnet.updated_at,
    // Keep original fields for API calls
    ...apiLeadMagnet,
  };
};

function LeadMagnetList({
  onEdit,
  refreshTrigger,
  onLeadMagnetsLoaded,
}) {
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLeadMagnet, setSelectedLeadMagnet] = useState(null);

  const loadLeadMagnets = useCallback(async (searchQuery = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await leadMagnetApi.leadMagnetListGet({
        search: searchQuery || null,
        status: null,
        createdBy: null,
        assetId: null,
        limit: 100,
        offset: 0,
      });
      
      // Axios wraps the response, so the actual data is in response.data
      const responseData = response?.data || response;
      const leadMagnetsList = responseData?.leadMagnets || [];
      
      // Transform from snake_case to camelCase
      const transformed = leadMagnetsList.map(transformLeadMagnet);
      
      setLeadMagnets(transformed);
      
      // Expose lead magnets to parent component if callback provided
      if (onLeadMagnetsLoaded) {
        onLeadMagnetsLoaded(leadMagnetsList);
      }
    } catch (err) {
      console.error('Error loading lead magnets:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load lead magnets';
      setError(errorMessage);
      setLeadMagnets([]);
    } finally {
      setLoading(false);
    }
  }, [onLeadMagnetsLoaded]);

  const handleDelete = useCallback((magnet) => {
    setSelectedLeadMagnet(magnet);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedLeadMagnet) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await leadMagnetApi.leadMagnetDeleteLeadMagnetIdDelete({
        leadMagnetId: selectedLeadMagnet.id,
      });
      
      setSuccess('Lead magnet deleted successfully');
      setShowDeleteModal(false);
      setSelectedLeadMagnet(null);
      // Reload the list
      await loadLeadMagnets(searchTerm.trim() || null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting lead magnet:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete lead magnet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedLeadMagnet, searchTerm, loadLeadMagnets]);

  const handleCopy = useCallback(async (magnet) => {
    try {
      setLoading(true);
      setError(null);

      // Build a clone payload from the row already loaded in state. The new
      // row starts as a Draft with no slug, the original internal name
      // suffixed with "(Copy)", and all content fields copied verbatim so the
      // admin can tweak the parts they need to change.
      const apiData = {
        internal_name: `${magnet.internalName || magnet.internal_name || 'Untitled'} (Copy)`,
        slug: null,
        status: 'DRAFT',
        page_title: magnet.pageTitle || magnet.page_title || '',
        hero_headline: magnet.heroHeadline || magnet.hero_headline || '',
        hero_subheadline: magnet.heroSubHeadline || magnet.hero_subheadline || null,
        bullet_points: magnet.bulletPoints || magnet.bullet_points || [],
        credibility_text: magnet.credibilityText || magnet.credibility_text || null,
        cta_text: magnet.formCtaText || magnet.cta_text || 'Download Now',
        seo_title: magnet.seoTitle || magnet.seo_title || null,
        seo_description: magnet.seoDescription || magnet.seo_description || null,
        asset_id: magnet.assetId || magnet.asset_id || null,
        page_type: (magnet.pageType || magnet.page_type || 'STANDARD').toUpperCase(),
        sound_bytes: magnet.soundBytes || magnet.sound_bytes || {},
        funnel_links: magnet.funnelLinks || magnet.funnel_links || [],
        cta_box: magnet.ctaBox || magnet.cta_box || {},
      };

      const response = await leadMagnetApi.leadMagnetCreatePost({
        leadMagnetCreate: apiData,
      });
      const created = response?.data || response;
      const newRow = created ? transformLeadMagnet(created) : null;

      setSuccess('Lead magnet copied. Opening for editing...');
      await loadLeadMagnets(searchTerm.trim() || null);

      if (newRow && onEdit) {
        onEdit(newRow);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error copying lead magnet:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to copy lead magnet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadLeadMagnets, onEdit, searchTerm]);

  const handlePublish = useCallback(async (magnet) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update status to PUBLISHED
      await leadMagnetApi.leadMagnetUpdateLeadMagnetIdPut({
        leadMagnetId: magnet.id,
        leadMagnetUpdate: {
          status: 'PUBLISHED',
        },
      });
      
      setSuccess('Lead magnet published successfully');
      // Reload the list
      await loadLeadMagnets(searchTerm.trim() || null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error publishing lead magnet:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to publish lead magnet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadLeadMagnets, searchTerm]);

  const isCerebralAdmin = currentUserHasCerebralAdminRole();
  
  const columns = useMemo(
    () => createLeadMagnetColumns({
      onEdit: isCerebralAdmin ? onEdit : null,
      onCopy: isCerebralAdmin ? handleCopy : null,
      onDelete: isCerebralAdmin ? handleDelete : null,
      onPublish: isCerebralAdmin ? handlePublish : null,
    }),
    [onEdit, handleCopy, handleDelete, handlePublish, isCerebralAdmin]
  );

  // Load data on mount, when refreshTrigger changes, or when search term changes (server-side filtering)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLeadMagnets(searchTerm.trim() || null);
    }, searchTerm ? 300 : 0); // Debounce search by 300ms, but load immediately on mount/refresh

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadLeadMagnets, refreshTrigger]);

  if (loading && leadMagnets.length === 0) {
    return <LoadingSpinner text="Loading lead magnets..." />
  }

  return (
    <div className="lead-magnet-list">
      {error && (
        <Alert
          variant="error"
          onClose={() => setError(null)}
          className="lead-magnet-list__alert"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          onClose={() => setSuccess(null)}
          className="lead-magnet-list__alert"
        >
          {success}
        </Alert>
      )}

      {/* Search Bar */}
      <div className="lead-magnet-list__search">
        <TextInput
          placeholder="Search by name, title, or slug..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <Button
            variant="ghost"
            className="lead-magnet-list__search-clear"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            ×
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="lead-magnet-list__table-container">
        <Table
          columns={columns}
          data={leadMagnets}
          keyExtractor={(row) => row.id}
          emptyMessage="No lead magnets found. Create your first lead magnet to get started."
        />
      </div>

      {/* Delete Modal */}
      {showDeleteModal && selectedLeadMagnet && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedLeadMagnet(null);
          }}
          title="Delete Lead Magnet"
          size="small"
          className="lead-magnet-list__modal"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedLeadMagnet(null);
                }}
                className="lead-magnet-list__btn"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                disabled={selectedLeadMagnet.status === 'published' || selectedLeadMagnet.status === 'PUBLISHED'}
                className="lead-magnet-list__btn"
              >
                Delete
              </Button>
            </>
          }
        >
          <p>
            Are you sure you want to delete "{selectedLeadMagnet.internalName || selectedLeadMagnet.internal_name}"?
            {(selectedLeadMagnet.status === 'published' || selectedLeadMagnet.status === 'PUBLISHED') && (
              <span className="lead-magnet-list__warning">
                This lead magnet is published. Only draft or archived lead magnets should be deleted.
              </span>
            )}
          </p>
        </Modal>
      )}
    </div>
  );
}

export default LeadMagnetList;

